/**
 * The builder on a phone — §7 of the plan.
 *
 * The desktop builder is a grid: seven columns that line up down the card so a
 * quantity sits under a quantity and a total under a total. A phone is 390
 * points wide and none of that survives. Squeezing it produced the thing the
 * screenshot showed — "O.", "B.", "N.", three pieces of text on top of each
 * other, and line rows with a quantity box and no name.
 *
 * So the phone does not get a narrower table. It gets **two screens**:
 *
 * - the **quote screen**, one card per visit, each a summary with a total and
 *   an Open;
 * - the **visit screen**, that visit's services as cards with the lines
 *   inside them, one line per row, name above and quantity beside.
 *
 * This is the same tree, the same mutations and the same editors — only the
 * arrangement changes, which is what "the layout folds, the components do not
 * fork" was always supposed to mean. Everything a finger has to hit is at
 * least 40px, every quantity opens the decimal keypad, and nothing scrolls
 * sideways.
 */
import type { MouseEvent, ReactNode } from 'react'
import {
  type QuoteLineKind, type QuoteServiceView, type QuoteTree, type QuoteVisitView,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { formatDollars, formatNumber, formatRate } from '../lib/format.js'
import { BuyingLine, QtyInput, VisitTile } from './quote-rows.js'
import { VisitMarginChip } from './visit-margin.js'
import { ChoiceChips } from './choices-field.js'
import {
  IconBillingPlans, IconChevronLeft, IconChevronRight, IconClock, IconEdit, IconFile, IconLock, IconMore,
  IconMoreVertical, IconPlus, IconVisitDay,
} from '../frame/icons.js'
import { KindTile } from '../builder/kind-tile.js'
import { ADD_LINK_LABELS, LineTag } from '../builder/rows/line-row.js'
import { VisitConnector } from '../builder/rows/visit-card.js'
import { MEASURE_DRIVES_LINES, OPTIONAL_LABEL, SERVICE_SUB } from '../builder/copy.js'

// ---------------------------------------------------------------------------
// The quote screen
// ---------------------------------------------------------------------------

/**
 * One visit, as a card you can read at arm's length.
 *
 * Everything the desktop card says in one row is here in four lines, in the
 * order somebody reads it: what it is, when it happens and how it bills, what
 * it costs, and what is on it. Opening it is the only action — every edit
 * lives one screen in, because a card with six tap targets on a phone is a
 * card you cannot tap.
 */
export function PhoneVisitCard({
  visit, targetMarginPct, onOpen, onMenu, onMargin, marginOpen = false, choices = [], onChoices,
}: {
  visit: QuoteVisitView
  targetMarginPct: number
  onOpen: () => void
  /** Absent on a quote that is not a draft — no ⋯ (walkthrough findings §4, folder 52). */
  onMenu?: (at: { x: number; y: number }) => void
  /** The footer's margin chip opens the visit's margin sheet (qb-09 · 2). */
  onMargin?: (anchor: HTMLElement) => void
  marginOpen?: boolean
  /** On a phone the choice chips sit in the footer, never the title row (§9.1). */
  choices?: string[]
  onChoices?: (event: MouseEvent<HTMLElement>) => void
}) {
  const trips = Number.isFinite(visit.occurrences) ? visit.occurrences : null
  const perVisit = trips && trips > 0 ? visit.hours / trips : visit.hours
  const lines = visit.services.reduce((sum, service) => sum + service.lines.length, 0)

  return (
    <section
      className={cn('overflow-hidden rounded-touch border bg-surface',
        visit.declined ? 'border-line opacity-60' : 'border-line')}
    >
      <div className="flex items-start gap-3 px-3.5 pt-3.5">
        <VisitTile role={visit.role} />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <b className="text-[15px] font-bold text-text">{visit.name}</b>
            {visit.optional && <PhoneTag tone="info">{OPTIONAL_LABEL}</PhoneTag>}
            {visit.declined && <PhoneTag>not taken</PhoneTag>}
          </span>
          <span className="mt-1 block truncate text-[12px] text-text-muted">
            {visit.phrase.card}
            {perVisit > 0 && ` · ${formatNumber(Math.round(perVisit * 10) / 10)} h`}
          </span>
        </span>

        <b className="tnum shrink-0 text-[15px] font-bold text-text">{formatDollars(visit.total)}</b>

        {onMenu && (
          <button
            type="button"
            aria-label={`${visit.name} options`}
            onClick={(event) => {
              const box = event.currentTarget.getBoundingClientRect()
              onMenu({ x: box.right - 8, y: box.bottom + 4 })
            }}
            className="-mr-1.5 -mt-1 flex h-9 w-8 shrink-0 items-center justify-center
                       text-text-faint"
          >
            <IconMoreVertical size={16} />
          </button>
        )}
      </div>

      {/*
        * Wraps rather than clipping: three plan chips beside the counts and the
        * margin chip are wider than 390, and the row used to push Open › off
        * its right-hand edge — the one control the card exists for.
        */}
      <footer className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-2 border-t border-line
                         bg-surface-alt px-3.5 py-2.5">
        <span className="min-w-0 flex-1 truncate text-[12px] text-text-muted">
          {visit.services.length} service{visit.services.length === 1 ? '' : 's'} · {lines} line{lines === 1 ? '' : 's'}
        </span>
        <ChoiceChips names={choices} onOpen={onChoices} />
        <VisitMarginChip
          pct={visit.marginPct}
          targetPct={targetMarginPct}
          open={marginOpen}
          onOpen={(anchor) => onMargin?.(anchor)}
          label={`${visit.name} margin`}
        />
        <button
          type="button"
          onClick={onOpen}
          className="flex shrink-0 items-center gap-0.5 text-[12.5px] font-semibold text-accent"
        >
          Open <IconChevronRight size={14} />
        </button>
      </footer>
    </section>
  )
}


/**
 * The connector between two phone visit cards (board cb-17 cell 1):
 * `↳ 1 business day after Diagnose is done` on the info tint, on a 2px rule
 * that runs from the card above to the card below.
 *
 * The desktop's own `VisitConnector`, not a copy of it — the board draws the
 * same chip at the same 30px. Only its placing differs: the phone cards sit
 * in a 12px-gap column, so the chip lands 6px under one card and 6px over the
 * next (the board's `margin: -6px 16px 6px`), and its rule is 27px in from
 * the card's edge rather than the desktop's 41. `-ml-3.5` takes the desktop's
 * 14px of extra indent back off, and `py-2` turns its −14px margins into the
 * board's 6px gaps. `overflow-x-clip` so a long phrase is cut at the card's
 * width rather than scrolling the screen sideways.
 */
export function PhoneVisitConnector({ phrase }: { phrase: string }) {
  return (
    <div data-phone-visit-connector="" className="-ml-3.5 flex flex-col overflow-x-clip py-2">
      <VisitConnector phrase={phrase} />
    </div>
  )
}


// ---------------------------------------------------------------------------
// The service screen (board qb-10 · 4)
// ---------------------------------------------------------------------------

/**
 * The service screen's own header (qb-10 · 4): back, the service's name over
 * `Service · Enhancements & irrigation`, and its ⋯.
 *
 * It stands in for the quote's header rather than sitting under it, because
 * the board's screen is the service's: one title, one back arrow, and that
 * arrow means "up one" like the quote's does.
 */
export function PhoneServiceHeader({
  service, onBack, onEditName, onMenu, backLabel,
}: {
  service: QuoteServiceView
  onBack: () => void
  onEditName: () => void
  /** The ⋯ — absent when there is nothing the viewer may do from it. */
  onMenu?: (at: { x: number; y: number }) => void
  /** What the back arrow says it goes to — the visit, or the quote. */
  backLabel: string
}) {
  return (
    <header className="flex shrink-0 items-center gap-2.5 bg-bg px-3 pb-2.5 pt-1">
      <button
        type="button"
        aria-label={backLabel}
        onClick={onBack}
        className="flex h-9 w-9 shrink-0 items-center justify-center text-text"
      >
        <IconChevronLeft size={22} />
      </button>
      <button type="button" onClick={onEditName} className="flex min-w-0 flex-1 flex-col text-left">
        <b className="truncate text-[16px] font-bold text-text">{service.name}</b>
        <span className="truncate text-[12px] text-text-muted">{SERVICE_SUB(service.divisionName)}</span>
      </button>
      {onMenu && (
        <button
          type="button"
          aria-label={`${service.name} options`}
          onClick={(event) => {
            const box = event.currentTarget.getBoundingClientRect()
            onMenu({ x: box.right - 8, y: box.bottom + 4 })
          }}
          className="flex h-9 w-9 shrink-0 items-center justify-center text-text"
        >
          <IconMore size={18} />
        </button>
      )}
    </header>
  )
}

/**
 * One service, in three shapes.
 *
 * - **Closed**: a row — name, lines, measure and total — that opens it.
 * - **Open** (the package builder's phone screen): the same card, open in
 *   place, with its lines under it. The measure gets a block of its own
 *   rather than a chip: on a measured service it is the number that drives
 *   every line below, the number somebody walked the site to get, and the one
 *   thing they will be typing in with muddy hands.
 * - **`screen`** (the quote's service screen, board qb-10 · 4): the body of a
 *   screen whose header is `PhoneServiceHeader` — a card carrying the measure
 *   (`Measure · drives the lines`) beside the service's total and its
 *   `margin 40.1% ▾` chip; then the lines as two-line rows in one card, and
 *   the four add links as a row of 34px pills that scrolls rather than clips
 *   (§10.3).
 */
export function PhoneServiceCard({
  service, canEdit, open, screen = false, onToggle, onEditName, onEditMeasure, onAdd, margin, children,
}: {
  service: QuoteServiceView
  canEdit: boolean
  open: boolean
  /** The quote's service screen (qb-10 · 4) rather than a card open in place. */
  screen?: boolean
  /** Closed: opens it. Open in place: closes it. */
  onToggle?: () => void
  /** Open in place: the name opens the service's editor (the screen's header does that there). */
  onEditName?: () => void
  onEditMeasure: () => void
  onAdd: (kind: QuoteLineKind, anchor: HTMLElement) => void
  /** `screen`: the chip beside the total, which opens the visit's margin sheet (§10.3). */
  margin?: {
    pct: number | null
    targetPct: number
    open: boolean
    onOpen: (anchor: HTMLElement) => void
  }
  /** The lines — open only. */
  children?: ReactNode
}) {
  /* Only a service the catalog measures has one (step 21 item 2). */
  const measured = Boolean(service.serviceId) && service.measurementTypeId !== null
  if (!open) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2.5 rounded-touch border border-line
                   bg-surface px-3.5 py-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <b className="block truncate text-[14px] font-semibold text-text">{service.name}</b>
          <span className="text-[12px] text-text-muted">
            {service.lines.length} line{service.lines.length === 1 ? '' : 's'}
            {service.measure !== null && service.measurementTypeId !== null && ` · ${formatNumber(service.measure)} ${
              service.measureUnit ?? ''}`}
          </span>
        </span>
        <b className="tnum shrink-0 text-[14px] font-bold text-text">
          {formatRate(service.total)}
        </b>
        <span aria-hidden className="shrink-0 text-text-faint"><IconChevronRight size={16} /></span>
      </button>
    )
  }

  if (screen) {
    return (
      <>
        <div className="px-4 pb-3">
          <div data-service-measure="" className="flex items-center gap-3 rounded-record border border-line
                                                  bg-surface px-3.5 py-3">
            {measured && (
              <button
                type="button"
                onClick={onEditMeasure}
                disabled={!canEdit}
                className="flex min-w-0 flex-col gap-0.5 text-left"
              >
                <span className="text-[11.5px] text-text-muted">{MEASURE_DRIVES_LINES}</span>
                <b className={cn('tnum text-[18px] font-bold',
                  service.measure === null ? 'text-warn' : 'text-text')}>
                  {service.measure === null ? '—' : formatNumber(service.measure)}
                  {service.measureUnit && (
                    <span className="text-[12.5px] font-medium text-text-muted"> {service.measureUnit}</span>
                  )}
                </b>
              </button>
            )}
            <div className="ml-auto flex shrink-0 flex-col items-end gap-1">
              <b className="tnum text-[17px] font-bold text-text">{formatRate(service.total)}</b>
              {margin && (
                <VisitMarginChip
                  pct={margin.pct}
                  targetPct={margin.targetPct}
                  open={margin.open}
                  onOpen={margin.onOpen}
                  label={`${service.name} margin`}
                />
              )}
            </div>
          </div>
        </div>

        <section data-service-lines="" className="mx-4 overflow-hidden rounded-record border border-line bg-surface">
          {children}

          {canEdit && (
            <footer className="no-scrollbar flex gap-1.5 overflow-x-auto border-t border-line-faint px-3.5 py-3">
              {ADD_KINDS.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  onClick={(event) => onAdd(kind, event.currentTarget)}
                  className="flex h-[34px] shrink-0 items-center whitespace-nowrap rounded-field border
                             border-line bg-surface px-[9px] text-[12.5px] font-semibold text-accent"
                >
                  + {ADD_LINK_LABELS[kind]}
                </button>
              ))}
            </footer>
          )}
        </section>
      </>
    )
  }

  return (
    <section className="overflow-hidden rounded-touch border border-accent-line bg-surface
                        ring-2 ring-accent-ring">
      <header className="flex items-start gap-2.5 px-3.5 pt-3.5">
        <button type="button" onClick={onEditName} disabled={!canEdit} className="min-w-0 flex-1 text-left">
          <b className="block truncate text-[15px] font-bold text-text">{service.name}</b>
          <span className="text-[12px] text-text-muted">{service.divisionName}</span>
        </button>
        <b className="tnum shrink-0 text-[15px] font-bold text-text">
          {formatRate(service.total)}
        </b>
        <button
          type="button"
          onClick={onToggle}
          aria-label={`Close ${service.name}`}
          className="-mr-1.5 -mt-1 flex h-9 w-8 shrink-0 items-center justify-center
                     text-text-faint"
        >
          <IconChevronRight size={16} />
        </button>
      </header>

      {measured && (
        <div className="mx-3.5 mt-3 flex items-center gap-3 rounded-field border
                        border-accent-line bg-accent-soft px-3 py-2.5">
          <span className="min-w-0 flex-1">
            <span className="block text-[11.5px] font-semibold text-accent-dark">
              Measure · {service.measureUnit ?? 'from service'}
            </span>
            <button type="button" onClick={onEditMeasure} disabled={!canEdit} className="flex items-baseline gap-1.5">
              <b className={cn('tnum text-[20px] font-bold',
                service.measure === null ? 'text-warn' : 'text-text')}>
                {service.measure === null ? 'Not set' : formatNumber(service.measure)}
              </b>
              <span className="text-[13px] text-text-muted">{service.measureUnit}</span>
              <span aria-hidden className="text-text-faint"><IconEdit size={13} /></span>
            </button>
          </span>
        </div>
      )}

      <div className="mt-2.5">{children}</div>

      {canEdit && (
        <footer className="flex items-center gap-1.5 overflow-x-auto border-t border-line
                           bg-surface-alt px-3.5 py-2.5">
          {(['item', 'labor', 'deployment', 'equipment', 'sub'] as const).map((kind) => (
            <button
              key={kind}
              type="button"
              onClick={(event) => onAdd(kind, event.currentTarget)}
              className="flex h-10 shrink-0 items-center gap-1 rounded-field border border-line
                         bg-surface px-3 text-[13px] font-semibold text-accent"
            >
              <IconPlus size={12} /> {LABELS[kind]}
            </button>
          ))}
        </footer>
      )}
    </section>
  )
}

const LABELS: Record<QuoteLineKind, string> = {
  item: 'Item',
  labor: 'Labor',
  deployment: 'Deployment',
  equipment: 'Equipment',
  sub: 'Sub',
}

/** The board's four, in its order: + Materials · + Labor · + Equipment · + Fees. */
const ADD_KINDS = ['item', 'labor', 'equipment', 'deployment'] as const

/**
 * One line, on a phone.
 *
 * Name above, quantity and total below — because at 390 points a name, a
 * quantity box, a unit, a rate and a total cannot share a row without one of
 * them being three characters wide. The rate is dropped rather than truncated:
 * it is one tap away in the editor, and a truncated price is worse than no
 * price.
 */
/**
 * A line on a phone — two lines, no columns (folder 44 §10.3, board qb-10).
 *
 * Line one is what it is and what the customer pays for it; line two reads
 * the desktop header's sum left to right — `5.17 bag × $55.11` — and then
 * ours, on the internal tint with the lock: `cost $211.04 · 25.9%`, amber
 * under the company's target. The name truncates; the sentence never does.
 * There is no ⋮: the name opens the line's sheet, which holds Reset and
 * Remove.
 */
export function PhoneLineRow({
  line, onQty, onOpen, targetPct, qtyCell, readOnly = false,
}: {
  line: QuoteTree['visits'][number]['services'][number]['lines'][number]
  onQty: (qty: number) => Promise<boolean> | void
  onOpen: () => void
  /** A quote that is not a draft: the quantity disabled, the name opens nothing (folder 52). */
  readOnly?: boolean
  targetPct: number
  /** A package's Qty rule in place of the number box — `LineRow`'s prop of the same name. */
  qtyCell?: ReactNode
}) {
  const under = line.marginPct !== null && line.marginPct < targetPct
  return (
    <>
    <div className="flex items-start gap-3 border-t border-line-faint bg-surface px-3.5 py-3">
      <KindTile kind={line.kind} size={32} />

      <div className="flex min-w-0 flex-1 flex-col gap-[7px]">
        <div className="flex min-w-0 items-center gap-2">
          <button type="button" data-line-name="" onClick={onOpen} disabled={readOnly} className="min-w-0 text-left">
            <b className="block truncate text-[14.5px] font-semibold text-text">
              {line.description}
            </b>
          </button>
          {/* qb-10 · 4's tag after the name — the desktop row's own (`LineRow`). */}
          {line.optional && <LineTag tone="info">{OPTIONAL_LABEL}</LineTag>}
          <b className="tnum ml-auto shrink-0 text-[14.5px] font-bold text-text">
            {formatRate(line.amount)}
          </b>
        </div>

        {/*
          * Each part keeps its words whole. Where a long cost cannot sit
          * beside the quantity at 390, the locked chip moves under it rather
          * than being cut — a sentence with its end clipped off is the
          * number you most needed.
          */}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5 whitespace-nowrap">
          {qtyCell ?? <QtyInput
            value={line.qty}
            onCommit={onQty}
            disabled={readOnly}
            label={`How much ${line.description}`}
            /*
             * The width as `width`, not in `className`: `controlClass` emits
             * `w-full` unless told otherwise, and a `w-[100px]` beside it lost —
             * the box took the whole row and the line's name had no room at all.
             */
            width="w-[80px] shrink-0"
            className="h-[30px] justify-end text-[14px] font-semibold"
            unit={line.unit}
          />}
          <span className="tnum text-[12.5px] text-text-muted">× {formatRate(line.rate)}</span>
          <span
            className={cn('tnum ml-auto inline-flex h-6 items-center gap-[5px] rounded-[7px]',
              'border border-line-soft bg-surface-internal px-2 text-[11.5px] text-text-muted')}
          >
            <IconLock size={11} className="text-text-faint" />
            cost {formatRate(line.cost)}
            <span className="text-text-faint">·</span>
            <b className={under ? 'text-warn' : 'text-text-muted'}>
              {line.marginPct === null ? '—' : `${line.marginPct.toFixed(1)}%`}
            </b>
          </span>
        </div>
      </div>
    </div>
    {/* How it is bought, the desktop row's own sentence (Board 2). */}
    {line.buying?.text && <BuyingLine buying={line.buying} phone />}
    </>
  )
}

// ---------------------------------------------------------------------------
// Chrome
// ---------------------------------------------------------------------------

/**
 * Build · Billing · Proposal · Activity, at the bottom (boards qb-09, qb-10,
 * cb-16, cb-17).
 *
 * The record page's phone tab bar, cell for cell: a 20px glyph in a 44 × 28
 * pill that is accent-soft on the tab you are on, over a 10.5px label —
 * bold and green when on, medium and muted when not — in a 58px row. A person
 * moving between a contact and a quote on a phone should not have to learn
 * two navigations. It sits above the safe band, and above the sticky total
 * bar, which is why the bar carries its own bottom padding.
 */
export function PhoneTabs({
  tab, onTab,
}: { tab: string; onTab: (next: string) => void }) {
  return (
    <nav aria-label="Quote" className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface pb-safe">
      <div className="flex px-1.5">
        {PHONE_TABS.map(({ key, Icon }) => {
          const on = key === tab
          return (
            <button
              key={key}
              type="button"
              onClick={() => onTab(key)}
              aria-current={on ? 'page' : undefined}
              className={cn(
                'flex h-[58px] flex-1 flex-col items-center justify-center gap-[3px] text-[10.5px] leading-none',
                on ? 'font-bold text-accent' : 'font-medium text-text-muted',
              )}
            >
              <span
                data-tab-pill=""
                className={cn('flex h-7 w-11 items-center justify-center rounded-control',
                  on && 'bg-accent-soft')}
              >
                <Icon size={20} />
              </span>
              <span>{key[0]!.toUpperCase() + key.slice(1)}</span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}

/** The boards' four glyphs: a calendar with a tick, a dollar, a page, a clock. */
const PHONE_TABS = [
  { key: 'build', Icon: IconVisitDay },
  { key: 'billing', Icon: IconBillingPlans },
  { key: 'proposal', Icon: IconFile },
  { key: 'activity', Icon: IconClock },
] as const

function PhoneTag({
  children, tone = 'plain',
}: { children: ReactNode; tone?: 'plain' | 'info' | 'choice' }) {
  return (
    <span
      className={cn(
        'inline-flex h-[18px] shrink-0 items-center whitespace-nowrap rounded-tag px-1.5',
        'text-[11px] font-semibold',
        tone === 'plain' && 'bg-line-soft text-text-muted',
        tone === 'info' && 'bg-info-bg text-info',
      )}
      style={tone === 'choice'
        ? {
          background: 'rgb(var(--status-quoteLine-deployment-bg))',
          color: 'rgb(var(--status-quoteLine-deployment-fg))',
        }
        : undefined}
    >
      {children}
    </span>
  )
}
